/**
 * N7 (86d3w2kf4) — relative-time copy for notification rows, as pure functions.
 *
 * <b>Why these take `nowMs` instead of calling `Date.now()`.</b> A notification row says things like
 * "next retry in 6m", and the retry it is counting down to is scheduled by the SERVER's clock. If the
 * browser is two minutes fast, an unskewed countdown shows "retrying now" while the job sits waiting,
 * and the user presses Retry on a posting that was about to retry itself. So every caller passes a
 * server-aligned instant (`useServerNow()`), and this file never reads a clock of its own — which is
 * also what makes it assertable in vitest without freezing time.
 *
 * Kept out of the component for the same reason `notificationMerge.ts` is: vitest runs in Node with
 * no jsdom, so anything worth asserting has to be callable without React.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long ago a row was created, in the shell's house voice: "now", "6m", "3h", "2d", then a date.
 *
 * ⚠️ Takes MICROSECONDS, matching `createdAtMicros` — the only ordering key on this wire. Divided to
 * milliseconds here rather than at the call sites, so no caller has to remember the unit.
 */
export function relativeFromMicros(createdAtMicros: number, nowMs: number): string {
  const thenMs = Math.floor(createdAtMicros / 1000);
  const elapsed = nowMs - thenMs;

  // A row created a moment ago can carry a timestamp a beat in the FUTURE — the server stamps it and
  // the skew estimate is only ever an estimate. "now" is the honest reading; a negative age is not.
  if (elapsed < MINUTE) return 'now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h`;
  if (elapsed < 7 * DAY) return `${Math.floor(elapsed / DAY)}d`;

  return new Date(thenMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * The countdown to a scheduled retry: "in 6m", "in 2h", or "any moment now" once it is due.
 *
 * Returns null when there is nothing scheduled, so the caller can omit the line entirely rather than
 * render an empty one.
 */
export function countdownTo(iso: string | null, nowMs: number): string | null {
  if (!iso) return null;

  const target = Date.parse(iso);
  if (Number.isNaN(target)) return null;

  const remaining = target - nowMs;
  // Due, or overdue because the dispatcher is busy. Either way the user's answer is "wait" — naming a
  // negative duration would just invite them to press Retry on a job already on its way.
  if (remaining <= 0) return 'any moment now';
  if (remaining < MINUTE) return 'in less than a minute';
  if (remaining < HOUR) return `in ${Math.round(remaining / MINUTE)}m`;

  return `in ${Math.round(remaining / HOUR)}h`;
}

/**
 * The absolute timestamp, for the tooltip behind the relative one.
 *
 * Rendered from the row's own `createdAt` string rather than the micros, because this is the one
 * place the display value and the ordering value are allowed to diverge: the user wants their local
 * wall clock here, not the server's.
 */
export function absoluteLabel(createdAt: string): string {
  const parsed = Date.parse(createdAt);
  if (Number.isNaN(parsed)) return createdAt;

  return new Date(parsed).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
